import { useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { useCamera } from "@src/features/cameras";
import { isLegacyAppCameraKey } from "@src/utils/key-parser";

export interface UseCameraRouteParamsOptions {
  /**
   * Whether siteId is required. If true and missing, will log an error.
   * @default true
   */
  requireSiteId?: boolean;
  /**
   * Whether cameraKey is required. If true and missing, will log an error.
   * @default true
   */
  requireCameraKey?: boolean;
  /**
   * Whether to fetch camera data to get effectiveSiteId from camera response.
   * If true, will use camera.site.id as fallback for siteId.
   * @default false
   */
  fetchCamera?: boolean;
}

export interface UseCameraRouteParamsResult {
  /**
   * siteId from URL params, or empty string if not provided
   */
  siteId: string;
  /**
   * cameraKey from URL params, or empty string if not provided
   */
  cameraKey: string;
  /**
   * Effective siteId - uses siteId from URL, or falls back to camera.site.id if fetchCamera is true
   */
  effectiveSiteId: string;
  /**
   * Whether required parameters are missing
   */
  hasMissingParams: boolean;
  /**
   * Whether this is a legacy app camera that doesn't exist in new API
   */
  isLegacyAppCamera: boolean;
  /**
   * Camera data if fetchCamera is true
   */
  camera?: ReturnType<typeof useCamera>["data"];
  /**
   * Camera loading state if fetchCamera is true
   */
  isCameraLoading?: boolean;
  /**
   * Camera fetching state if fetchCamera is true
   */
  isCameraFetching?: boolean;
  /**
   * Camera error if fetchCamera is true
   */
  cameraError?: ReturnType<typeof useCamera>["error"];
  /**
   * Camera refetch function if fetchCamera is true
   */
  refetchCamera?: ReturnType<typeof useCamera>["refetch"];
}

/**
 * Hook to extract and validate camera route parameters (siteId, cameraKey) from URL.
 * Centralizes the logic for handling these parameters across different screens.
 *
 * @param options - Configuration options
 * @returns Route parameters and validation state
 *
 * @example
 * // Basic usage - both required
 * const { siteId, cameraKey, effectiveSiteId, hasMissingParams } = useCameraRouteParams();
 *
 * @example
 * // With camera data fetching for effectiveSiteId fallback
 * const { siteId, cameraKey, effectiveSiteId, camera, isCameraLoading } = useCameraRouteParams({
 *   fetchCamera: true
 * });
 *
 * @example
 * // Optional parameters
 * const { siteId, cameraKey } = useCameraRouteParams({
 *   requireSiteId: false,
 *   requireCameraKey: false
 * });
 */
export function useCameraRouteParams(options: UseCameraRouteParamsOptions = {}): UseCameraRouteParamsResult {
  const { requireSiteId = true, requireCameraKey = true, fetchCamera = false } = options;

  const params = useLocalSearchParams<{
    siteId?: string;
    cameraKey?: string;
  }>();

  const siteId = params.siteId || "";
  const cameraKey = params.cameraKey || "";
  const isLegacy = isLegacyAppCameraKey(cameraKey);

  const hasMissingParams = useMemo(() => {
    const missingSiteId = requireSiteId && !siteId;
    const missingCameraKey = requireCameraKey && !cameraKey;

    if (missingSiteId || missingCameraKey) {
      const missing: string[] = [];
      if (missingSiteId) missing.push("siteId");
      if (missingCameraKey) missing.push("cameraKey");
      console.error(`Missing required URL parameters: ${missing.join(", ")}`);
      return true;
    }

    return false;
  }, [requireSiteId, requireCameraKey, siteId, cameraKey]);

  // Skip camera fetch for G1 cameras - they don't exist in the new API
  const shouldFetchCamera = fetchCamera && !isLegacy;

  const cameraQuery = useCamera(
    shouldFetchCamera && siteId && cameraKey ? siteId : "",
    shouldFetchCamera && cameraKey ? cameraKey : "",
  );

  const effectiveSiteId = useMemo(() => {
    if (siteId) return siteId;
    if (fetchCamera && cameraQuery.data?.site?.id) return cameraQuery.data.site.id;
    return "";
  }, [siteId, fetchCamera, cameraQuery.data?.site?.id]);

  return {
    siteId,
    cameraKey,
    effectiveSiteId,
    hasMissingParams,
    isLegacyAppCamera: isLegacy,
    ...(fetchCamera && {
      camera: cameraQuery.data,
      isCameraLoading: cameraQuery.isLoading,
      isCameraFetching: cameraQuery.isFetching,
      cameraError: cameraQuery.error,
      refetchCamera: cameraQuery.refetch,
    }),
  };
}
